import React, { useState, useEffect, useMemo } from 'react';
import ApiClient from '../utils/ApiClient';
import ManageAsinsModal from '../components/ManageAsinsModal';
import SfpRackModal from '../components/ReceiveInventory/SfpRackModal';

export default function ManageSfpInventory() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');
    const [pushing, setPushing] = useState(false);
    const [pushResult, setPushResult] = useState(null);
    const [asinModalProduct, setAsinModalProduct] = useState(null);
    const [refreshingRanks, setRefreshingRanks] = useState(false);
    const [creatingListings, setCreatingListings] = useState(false);
    // Owner 2026-09-01 (PO -> local split): units assigned from a PO flow through here —
    // expected (no space yet) -> placed (advertised) -> sale pending -> sold. Placing a unit
    // HERE is the moment the Amazon quantity push fires — never earlier. Revoking retires an
    // expected unit; placed units are physical and are never revoked from this page.
    const [pipelineRows, setPipelineRows] = useState([]);
    const [sfpLocations, setSfpLocations] = useState([]);
    const [placeLocationByKey, setPlaceLocationByKey] = useState({});
    const [placeQtyByKey, setPlaceQtyByKey] = useState({});
    const [placingKey, setPlacingKey] = useState(null);
    const [placeMessage, setPlaceMessage] = useState(null);
    const [revokeQtyByKey, setRevokeQtyByKey] = useState({});
    const [revokingKey, setRevokingKey] = useState(null);
    // Owner 2026-09-13: we are not in SFP yet; the bar is 100 merchant-fulfilled units a month.
    const [volume, setVolume] = useState(null);
    const [showTopAsins, setShowTopAsins] = useState(false);

    const [putAwayRows, setPutAwayRows] = useState([]);
    const [rackLine, setRackLine] = useState(null);

    // PO SFP split (spec 2026-10-10 §E): received SFP units not yet in an SFP bin, across POs.
    const loadPutAway = async () => {
        try {
            const res = await ApiClient.get('/api/local-sfp/put-away');
            setPutAwayRows(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to load SFP put-away list:', err);
        }
    };

    const fetchPipeline = async () => {
        try {
            const res = await ApiClient.get('/api/local-sfp/pipeline');
            setPipelineRows(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to load local-SFP pipeline:', err);
        }
    };

    const fetchVolume = async () => {
        try {
            const res = await ApiClient.get('/api/local-sfp/mfn-volume');
            setVolume(res?.data && Array.isArray(res.data.months) ? res.data : null);
        } catch (err) {
            console.error('Failed to load MFN volume:', err);
        }
    };

    const fetchSfpLocations = async () => {
        try {
            const res = await ApiClient.post('/api/sflocalinventorylocations');
            const all = Array.isArray(res?.data) ? res.data : [];
            // SFP-section pallet spaces only — placement into a returns bin is refused
            // server-side anyway; the dropdown simply never offers it.
            setSfpLocations(all.filter(l => l.section_type === 'SFP'));
        } catch (err) {
            console.error('Failed to load SFP locations:', err);
        }
    };

    const placeGroup = async (g) => {
        const key = `${g.poNumber}|${g.asin}`;
        const location = placeLocationByKey[key];
        const qty = placeQtyByKey[key] ?? g.expected;
        if (!location || !qty) return;
        setPlacingKey(key);
        setPlaceMessage(null);
        try {
            const res = await ApiClient.post('/api/local-sfp/place',
                { poNumber: g.poNumber, asin: g.asin, qty, location });
            const push = (res.data?.push || []).map(p => p.message).filter(Boolean).join('; ');
            setPlaceMessage(`Placed ${res.data?.placed} unit(s) of ${g.asin} into ${location}.`
                + (push ? ` Amazon: ${push}` : ''));
            await Promise.all([fetchPipeline(), fetchSummary()]);
        } catch (err) {
            setPlaceMessage(err.response?.data?.error || err.message);
        } finally {
            setPlacingKey(null);
        }
    };

    const revokeGroup = async (g) => {
        const key = `${g.poNumber}|${g.asin}`;
        const qty = revokeQtyByKey[key] ?? g.expected;
        if (!qty) return;
        if (!window.confirm(`Revoke ${qty} expected unit(s) of ${g.asin} from PO ${g.poNumber}? `
            + 'They return to the PO\'s remaining quantity for FBA. Placed units are not affected.')) {
            return;
        }
        setRevokingKey(key);
        setPlaceMessage(null);
        try {
            const res = await ApiClient.post('/api/local-sfp/revoke',
                { poNumber: g.poNumber, asin: g.asin, qty });
            setPlaceMessage(`Revoked ${res.data?.revoked} expected unit(s) of ${g.asin} from PO ${g.poNumber}.`);
            await fetchPipeline();
        } catch (err) {
            setPlaceMessage(err.response?.data?.error || err.message);
        } finally {
            setRevokingKey(null);
        }
    };

    // ASINs from the last push that had no SFP listing yet — offer to create them.
    const missingAsins = useMemo(() => {
        if (!pushResult?.results) return [];
        return [...new Set(pushResult.results.filter(r => r.missingListing && r.asin).map(r => r.asin))];
    }, [pushResult]);

    const handleCreateMissing = async () => {
        if (missingAsins.length === 0) return;
        setCreatingListings(true);
        try {
            const res = await ApiClient.post('/api/sfp-inventory/create-listings', { asins: missingAsins });
            const created = res?.data?.created ?? 0;
            alert(`Created ${created} SFP listing(s). Re-run "Update Amazon Quantities" to stock them.`);
            await fetchSummary();
        } catch (err) {
            console.error('Create listings failed:', err);
            alert('Failed to create listings');
        } finally {
            setCreatingListings(false);
        }
    };

    const handleRefreshRanks = async () => {
        setRefreshingRanks(true);
        try {
            await ApiClient.post('/api/sfp-inventory/refresh-ranks');
            await fetchSummary();
        } catch (err) {
            console.error('Rank refresh failed:', err);
        } finally {
            setRefreshingRanks(false);
        }
    };

    const fetchSummary = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiClient.post('/api/sfp-inventory/summary');
            setRows(Array.isArray(response?.data) ? response.data : []);
        } catch (err) {
            console.error('Error loading SFP inventory:', err);
            setError('Failed to load SFP inventory');
        } finally {
            setLoading(false);
        }
    };

    const handlePushQuantities = async () => {
        if (!window.confirm(
            'Push these on-hand quantities to Amazon for each ASIN\'s SFP New AND Used–Like New listings?\n\n'
            + 'Both conditions are set to the full count (they share the same physical stock), so keep '
            + 'quantities in sync by re-running after sales.')) {
            return;
        }
        setPushing(true);
        setPushResult(null);
        try {
            const response = await ApiClient.post('/api/sfp-inventory/push-quantities');
            setPushResult(response?.data || null);
        } catch (err) {
            console.error('Error pushing quantities:', err);
            setPushResult({ success: false, message: 'Failed to push quantities' });
        } finally {
            setPushing(false);
        }
    };

    useEffect(() => {
        fetchSummary();
        fetchPipeline();
        loadPutAway();
        fetchSfpLocations();
        fetchVolume();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.asin || '').toLowerCase().includes(q) ||
            (r.gtin || '').toLowerCase().includes(q) ||
            (r.item_code || '').toLowerCase().includes(q) ||
            (r.product_title || '').toLowerCase().includes(q)
        );
    }, [rows, filter]);

    const totalUnits = useMemo(
        () => filtered.reduce((sum, r) => sum + (Number(r.qty) || 0), 0),
        [filtered]
    );

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Owner 2026-09-13: not in SFP yet — the bar Amazon holds us to is 100 merchant-
                fulfilled units a month. One axis, one hue per SKU family, target as a rule. */}
            {volume && (
                <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <h2 className="text-sm font-semibold text-gray-900">Merchant-fulfilled volume toward SFP</h2>
                            <p className="text-xs text-gray-600">
                                Units on merchant-fulfilled orders per month by condition (cancellations excluded). Target {volume.target}/month.
                            </p>
                        </div>
                        <div className="flex gap-6">
                            <div>
                                <div className="text-2xl font-bold text-gray-900" data-testid="mfn-month-units">{volume.currentMonth.units}</div>
                                <div className="text-xs text-gray-600">so far in {volume.currentMonth.month} (day {volume.currentMonth.daysElapsed} of {volume.currentMonth.daysInMonth})</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-gray-900" data-testid="mfn-month-new">{volume.currentMonth.newUnits}</div>
                                <div className="text-xs text-gray-600">of them New condition</div>
                            </div>
                            <div>
                                <div className={`text-2xl font-bold ${volume.currentMonth.projected >= volume.target ? 'text-green-700' : 'text-gray-900'}`} data-testid="mfn-month-projected">{volume.currentMonth.projected}</div>
                                <div className="text-xs text-gray-600">projected at this pace</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-gray-900">{volume.target}</div>
                                <div className="text-xs text-gray-600">target</div>
                            </div>
                        </div>
                    </div>
                    {(() => {
                        const max = Math.max(volume.target, ...volume.months.map(m => m.units || 0)) * 1.1;
                        const pct = v => `${Math.max(0, (v / max) * 100)}%`;
                        return (
                            <div className="mt-4">
                                <div className="relative h-40" role="img" aria-label="Merchant-fulfilled units per month, trailing twelve months">
                                    <div className="absolute inset-x-0 border-t border-dashed border-gray-500" style={{ bottom: pct(volume.target) }}>
                                        <span className="absolute right-0 -top-4 text-[10px] text-gray-600">target {volume.target}</span>
                                    </div>
                                    <div className="absolute inset-0 flex items-end gap-2">
                                        {volume.months.map(m => (
                                            <div key={m.month} className="flex-1 h-full flex flex-col-reverse gap-px"
                                                title={`${m.month}: ${m.units} units (${m.newUnits} New, ${m.usedUnits} Used, ${m.otherUnits} other) on ${m.orders} orders`}>
                                                <>
                                                    <div style={{ height: pct(m.newUnits), backgroundColor: '#2a78d6', minHeight: m.newUnits ? 2 : 0 }} className="w-full rounded-t-sm" />
                                                    <div style={{ height: pct(m.usedUnits), backgroundColor: '#eb6834', minHeight: m.usedUnits ? 2 : 0 }} className="w-full rounded-t-sm" />
                                                    <div style={{ height: pct(m.otherUnits), backgroundColor: '#1baf7a', minHeight: m.otherUnits ? 2 : 0 }} className="w-full rounded-t-sm" />
                                                </>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-1">
                                    {volume.months.map(m => (
                                        <div key={m.month} className="flex-1 text-center text-[10px] text-gray-600">
                                            <div>{m.month.slice(5)}</div>
                                            <div className="font-medium text-gray-800">{m.units}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-700">
                                    <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: '#2a78d6' }} />New condition</span>
                                    <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: '#eb6834' }} />Used condition (MER returns)</span>
                                    <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: '#1baf7a' }} />Condition unknown</span>
                                    <button type="button" onClick={() => setShowTopAsins(v => !v)} className="ml-auto text-indigo-600 hover:text-indigo-500 font-medium">
                                        {showTopAsins ? 'Hide' : 'Show'} this month by ASIN
                                    </button>
                                </div>
                                {showTopAsins && (
                                    <div className="mt-3 overflow-x-auto max-h-64 overflow-y-auto">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">ASIN</th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Product</th>
                                                    <th className="px-3 py-1.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Units</th>
                                                    <th className="px-3 py-1.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">of which New</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {volume.currentMonth.topAsins.length === 0 ? (
                                                    <tr><td colSpan={4} className="px-3 py-3 text-center text-gray-500">No merchant-fulfilled orders yet this month.</td></tr>
                                                ) : volume.currentMonth.topAsins.map(a => (
                                                    <tr key={a.asin}>
                                                        <td className="px-3 py-1.5 font-mono text-gray-900">{a.asin}</td>
                                                        <td className="px-3 py-1.5 text-gray-700 max-w-md"><div className="line-clamp-1" title={a.productName || ''}>{a.productName || '—'}</div></td>
                                                        <td className="px-3 py-1.5 text-right text-gray-900">{a.units}</td>
                                                        <td className="px-3 py-1.5 text-right text-gray-700">{a.newUnits}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            )}

            {putAwayRows.length > 0 && (
                <div data-testid="put-away-section" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <h2 className="text-sm font-semibold text-amber-900 mb-2">
                        Put away — {putAwayRows.reduce((s, r) => s + r.rackable, 0)} SFP units waiting for a bin
                    </h2>
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-amber-800">
                                <th className="py-1 pr-3">PO</th><th className="py-1 pr-3">ASIN</th>
                                <th className="py-1 pr-3">Product</th><th className="py-1 pr-3 text-right">Units</th>
                                <th className="py-1 pr-3 text-right">Waiting</th><th className="py-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {putAwayRows.map(r => (
                                <tr key={`${r.poNumber}|${r.asin}`} className="border-t border-amber-100">
                                    <td className="py-1.5 pr-3">{r.poNumber}</td>
                                    <td className="py-1.5 pr-3">{r.asin}</td>
                                    <td className="py-1.5 pr-3">{r.productName}</td>
                                    <td className="py-1.5 pr-3 text-right">{r.rackable}</td>
                                    <td className="py-1.5 pr-3 text-right">{r.daysWaiting == null ? '—' : `${r.daysWaiting}d`}</td>
                                    <td className="py-1.5 text-right">
                                        <button type="button" onClick={() => setRackLine(r)}
                                                className="rounded-md bg-teal-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-teal-500">
                                            Rack ({r.rackable})
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* The modal closes itself via onClose; onRacked only refreshes, so a push warning stays visible. */}
            {rackLine && (
                <SfpRackModal line={rackLine} onClose={() => setRackLine(null)}
                              onRacked={() => { loadPutAway(); fetchPipeline(); }} />
            )}

            {/* Owner 2026-09-01: units assigned from a PO in the shipment planner get their
                warehouse space here — and that placement is when the Amazon quantity push fires. */}
            {pipelineRows.length > 0 && (
                <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50/50 p-4">
                    <h2 className="text-sm font-semibold text-teal-900 mb-1">
                        PO units assigned to local SFP
                    </h2>
                    <p className="text-xs text-gray-600 mb-3">
                        Assigned in the shipment planner. Placing an expected unit into an SFP space pushes
                        the quantity to Amazon — a unit with no space is never advertised. Revoking returns
                        an expected unit to the PO's remaining quantity.
                    </p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm bg-white rounded-md border border-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">PO</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">ASIN</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Product</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">SKU</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Expected</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Placed</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Sale pending</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Sold</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Place</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Revoke</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pipelineRows.map(g => {
                                    const key = `${g.poNumber}|${g.asin}`;
                                    const hasExpected = (g.expected || 0) > 0;
                                    return (
                                        <tr key={key}>
                                            <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">PO {g.poNumber}</td>
                                            <td className="px-3 py-2 font-mono text-xs text-gray-900">{g.asin}</td>
                                            <td className="px-3 py-2 text-gray-700 max-w-xs">
                                                <div className="line-clamp-2" title={g.productTitle || ''}>{g.productTitle || '—'}</div>
                                                {g.itemCode && <div className="font-mono text-xs text-gray-500">{g.itemCode}</div>}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs text-gray-500">{g.sellerSku}</td>
                                            <td className="px-3 py-2 text-right text-gray-900">{g.expected}</td>
                                            <td className="px-3 py-2 text-right text-gray-900">{g.placed}</td>
                                            <td className="px-3 py-2 text-right text-gray-700">{g.salePending}</td>
                                            <td className="px-3 py-2 text-right text-gray-700">{g.sold}</td>
                                            <td className="px-3 py-2">
                                                {hasExpected ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={g.expected}
                                                            aria-label={`qty to place for ${g.asin}`}
                                                            value={placeQtyByKey[key] ?? g.expected}
                                                            onChange={e => setPlaceQtyByKey(prev => ({
                                                                ...prev, [key]: Math.min(parseInt(e.target.value, 10) || 0, g.expected) }))}
                                                            className="w-16 rounded border-gray-300 text-sm text-right"
                                                        />
                                                        <select
                                                            aria-label={`place into location for ${g.asin}`}
                                                            value={placeLocationByKey[key] || ''}
                                                            onChange={e => setPlaceLocationByKey(prev => ({ ...prev, [key]: e.target.value }))}
                                                            className="rounded border-gray-300 text-sm"
                                                        >
                                                            <option value="">Place into…</option>
                                                            {sfpLocations.map(l => (
                                                                <option key={l.location} value={l.location}>{l.location}</option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            onClick={() => placeGroup(g)}
                                                            disabled={placingKey === key || !placeLocationByKey[key]}
                                                            className="px-3 py-1.5 text-xs rounded-md bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap"
                                                        >
                                                            {placingKey === key ? 'Placing…' : `Place ${placeQtyByKey[key] ?? g.expected}`}
                                                        </button>
                                                    </div>
                                                ) : <span className="text-xs text-gray-400">nothing to place</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                {hasExpected ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={g.expected}
                                                            aria-label={`qty to revoke for ${g.asin}`}
                                                            value={revokeQtyByKey[key] ?? g.expected}
                                                            onChange={e => setRevokeQtyByKey(prev => ({
                                                                ...prev, [key]: Math.min(parseInt(e.target.value, 10) || 0, g.expected) }))}
                                                            className="w-16 rounded border-gray-300 text-sm text-right"
                                                        />
                                                        <button
                                                            onClick={() => revokeGroup(g)}
                                                            disabled={revokingKey === key}
                                                            className="px-3 py-1.5 text-xs rounded-md bg-white text-red-700 ring-1 ring-inset ring-red-300 font-medium hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
                                                        >
                                                            {revokingKey === key ? 'Revoking…' : `Revoke ${revokeQtyByKey[key] ?? g.expected}`}
                                                        </button>
                                                    </div>
                                                ) : <span className="text-xs text-gray-400">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {placeMessage && (
                        <p className="mt-2 text-xs text-gray-700">{placeMessage}</p>
                    )}
                </div>
            )}
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Manage SFP Inventory</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Unique ASINs and total on-hand units across all Seller Fulfilled Prime (Y/Z) locations.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex gap-3">
                    <button
                        type="button"
                        onClick={fetchSummary}
                        disabled={loading || pushing}
                        className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                    <button
                        type="button"
                        onClick={handleRefreshRanks}
                        disabled={refreshingRanks || loading}
                        title="Recompute sales ranks from the offer feed (also runs nightly). Takes a few seconds per product."
                        className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {refreshingRanks ? 'Refreshing ranks…' : 'Refresh Ranks'}
                    </button>
                    <button
                        type="button"
                        onClick={handlePushQuantities}
                        disabled={pushing || loading || rows.length === 0}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {pushing ? 'Updating Amazon…' : 'Update Amazon Quantities'}
                    </button>
                </div>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 gap-4 mb-6 sm:max-w-md">
                <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-indigo-900">{filtered.length}</div>
                    <div className="text-sm text-indigo-700">Unique ASINs</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-900">{totalUnits}</div>
                    <div className="text-sm text-green-700">Total Units</div>
                </div>
            </div>

            {/* Filter */}
            <div className="mb-4">
                <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by ASIN, UPC, item code, or title…"
                    className="block w-full sm:w-96 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
            </div>

            {error && (
                <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
            )}

            {/* Push-to-Amazon results */}
            {pushResult && (
                <div className={`mb-6 rounded-lg border-l-4 p-4 ${pushResult.success === false ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'}`}>
                    {pushResult.success === false ? (
                        <p className="text-sm text-red-700"><strong>Error:</strong> {pushResult.message}</p>
                    ) : (
                        <>
                            <p className="text-sm text-gray-800 font-medium">
                                Amazon update complete — {pushResult.updated} listing(s) updated
                                {pushResult.skipped ? `, ${pushResult.skipped} skipped` : ''}
                                {pushResult.errors ? `, ${pushResult.errors} failed` : ''}.
                            </p>
                            {missingAsins.length > 0 && (
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        onClick={handleCreateMissing}
                                        disabled={creatingListings}
                                        className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50"
                                    >
                                        {creatingListings ? 'Creating listings…' : `Create missing SFP listings (${missingAsins.length})`}
                                    </button>
                                    <span className="ml-2 text-xs text-gray-500">
                                        {missingAsins.length} ASIN(s) have no SFP listing yet
                                    </span>
                                </div>
                            )}
                            {Array.isArray(pushResult.results) && pushResult.results.length > 0 && (
                                <div className="mt-3 overflow-x-auto max-h-72 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-300 text-sm">
                                        <thead className="bg-white/60 sticky top-0">
                                            <tr>
                                                <th className="py-2 pl-2 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">ASIN</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">SKU</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Condition</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Qty</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {pushResult.results.map((r, i) => (
                                                <tr key={i}>
                                                    <td className="py-1.5 pl-2 pr-3 font-mono text-gray-900">{r.asin || '—'}</td>
                                                    <td className="px-3 py-1.5 font-mono text-gray-600">{r.sku || '—'}</td>
                                                    <td className="px-3 py-1.5 text-gray-600">{r.condition || '—'}</td>
                                                    <td className="px-3 py-1.5 text-right text-gray-900">{r.qty}</td>
                                                    <td className="px-3 py-1.5">
                                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                                            r.status === 'success' ? 'bg-green-100 text-green-700'
                                                                : r.status === 'skipped' ? 'bg-yellow-100 text-yellow-700'
                                                                : 'bg-red-100 text-red-700'}`}>
                                                            {r.status}
                                                        </span>
                                                        {r.status !== 'success' && r.message ? <span className="ml-2 text-xs text-gray-500">{r.message}</span> : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                    <button type="button" onClick={() => setPushResult(null)} className="mt-3 text-sm text-indigo-600 hover:text-indigo-500 font-medium">Dismiss</button>
                </div>
            )}

            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">ASIN</th>
                                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Product</th>
                                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Item Code</th>
                                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">UPC / GTIN</th>
                                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Sales Rank</th>
                                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Qty in SFP</th>
                                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">ASINs</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">Loading…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No SFP inventory found.</td></tr>
                            ) : (
                                filtered.map((r, i) => (
                                    <tr key={r.gtin || i} className="even:bg-gray-50">
                                        <td className="py-3 pl-4 pr-3 text-sm sm:pl-6">
                                            {(r.asins && r.asins.length ? r.asins : [{ asin: r.asin, is_primary: 1 }]).map((a) => (
                                                <div key={a.asin} className={`font-mono ${a.is_primary ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                                                    {a.asin || '—'}{!a.is_primary && <span className="ml-1 text-xs text-gray-400">(linked)</span>}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-gray-700 max-w-md">
                                            <div className="line-clamp-2" title={r.product_title || ''}>{r.product_title || '—'}</div>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-3 text-sm font-mono text-gray-700">{r.item_code || '—'}</td>
                                        <td className="whitespace-nowrap px-3 py-3 text-sm font-mono text-gray-500">{r.gtin || '—'}</td>
                                        <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                                            {(r.asins && r.asins.length ? r.asins : [{ asin: r.asin, sales_rank: r.sales_rank, is_primary: 1, rank_updated_at: r.rank_updated_at }]).map((a) => (
                                                <div key={a.asin} className={a.is_primary ? 'text-gray-700' : 'text-gray-500'}
                                                    title={a.rank_updated_at ? `Updated ${a.rank_updated_at}` : 'Not yet ranked — use Refresh Ranks'}>
                                                    {a.sales_rank ? Number(a.sales_rank).toLocaleString() : '—'}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-900">{r.qty}</td>
                                        <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                                            <button
                                                type="button"
                                                onClick={() => setAsinModalProduct(r)}
                                                disabled={!r.gtin}
                                                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-40"
                                            >
                                                Manage
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ManageAsinsModal
                open={!!asinModalProduct}
                product={asinModalProduct}
                onClose={() => { setAsinModalProduct(null); fetchSummary(); }}
            />
        </div>
    );
}
